import { Alert } from '@ksyos/design-system';
import { useContext, useEffect, useState } from 'react';
import type {
    IAddCarePathServiceConsumerSettings,
    IAddCarePathServiceProviderSettings,
    IAddCarePathServiceSupervisorSettings,
    IAddCarePathSettings,
    IService,
    IServiceOrganizationSettings,
} from '@common/interfaces';
import type { ValidationErrors } from '@frontend/utils/';
import { ManagerAuthorization, ServiceCode, ServiceType } from '@common/enums/';
import {
    compareByNestedServiceTypeSortOrderAndCode,
    compareByServiceCodeSortOrder,
} from '@common/utils/serviceSortUtils';
import { addCarePathServiceSettingsSchema } from '@common/validation/addCarePath';
import { translateKey } from '@frontend/translate';
import {
    assertHasAuthorization,
    emptyValidationErrors,
    errorsDeepCompareValue,
    initializeServiceOrganizationSettings,
    scrollToValidationError,
    updateValidationErrorsForOne,
    useAbortableFetch,
} from '@frontend/utils/';
import type { IEmployment } from '../../../../interfaces/employment';
import type { IOrganization } from '../../../../interfaces/organization';
import type {
    IOrganizationProfile,
    IServiceGroupWithProfiles,
} from '../../../../interfaces/organizationAddCarePathState';
import type {
    IOrganizationServiceProvidersSettings,
    RealServiceOrganizationSettings,
} from '../../../carePath/settings/ServiceOrganizationSettings';
import { ServiceOrganizationSettings } from '../../../carePath/settings/ServiceOrganizationSettings';
import { getSettingsFromProfile } from '../helperFunctions/saveCarePath';
import { OrganizationAddCarePathContext } from '../organizationAddCarePathContext';

export interface ISettingsProps {
    employments: IEmployment[];
    errors?: ValidationErrors;
}

export function OrganizationAddCarePathServiceOrganizationSettings(props: ISettingsProps) {
    const abortableFetch = useAbortableFetch();
    const { addCarePathState, dispatchAddCarePathAction } = useContext(OrganizationAddCarePathContext);
    const [organization, setOrganization] = useState<IOrganization>();
    const [serviceConsumerDefaultSettings, setServiceConsumerDefaultSettings] = useState<
        (IAddCarePathServiceConsumerSettings & { profileId: string; service: IService })[] | undefined
    >(undefined);
    const [serviceProviderDefaultSettings, setServiceProviderDefaultSettings] = useState<
        (IAddCarePathServiceProviderSettings & { profileId: string; service: IService })[] | undefined
    >(undefined);
    const [serviceSupervisorDefaultSettings, setServiceSupervisorDefaultSettings] = useState<
        (IAddCarePathServiceSupervisorSettings & { profileId: string; service: IService })[] | undefined
    >(undefined);
    const [organizationServiceProvidersSettings, setOrganizationServiceProvidersSettings] =
        useState<IOrganizationServiceProvidersSettings>({});
    const [saveErrorsCounter, setSaveErrorsCounter] = useState(0);
    const [errors, setErrors] = useState<ValidationErrors>(emptyValidationErrors);
    const [initializeReady, setInitializeReady] = useState(false);
    assertHasAuthorization(ManagerAuthorization.organizationAddEdit);

    useEffect(() => {
        load();
    }, []);

    useEffect(() => {
        prepareOrganizationServiceProvidersSettings();
    }, [serviceProviderDefaultSettings]);

    useEffect(() => {
        if (
            serviceConsumerDefaultSettings !== undefined &&
            serviceProviderDefaultSettings !== undefined &&
            serviceSupervisorDefaultSettings !== undefined
        ) {
            initialize();
        }
    }, [serviceConsumerDefaultSettings, serviceProviderDefaultSettings, serviceSupervisorDefaultSettings]);

    useEffect(() => {
        setErrors(props.errors ?? emptyValidationErrors);
        setSaveErrorsCounter((counter) => counter + 1);
    }, [errorsDeepCompareValue(props.errors)]);

    useEffect(() => {
        if (errors && errors.size > 0) {
            scrollToValidationError(true);
        }
    }, [saveErrorsCounter]);

    if (!organization && addCarePathState.serviceGroups) {
        return null;
    }

    if (
        addCarePathState.serviceOrganizationSettings &&
        addCarePathState.serviceOrganizationSettings.every((item) => item.service.code.startsWith('OSAS'))
    ) {
        addCarePathState.serviceOrganizationSettings.sort(compareByServiceCodeSortOrder);
    } else if (addCarePathState.serviceOrganizationSettings) {
        addCarePathState.serviceOrganizationSettings.sort(compareByNestedServiceTypeSortOrderAndCode);
    }

    return (
        <>
            {initializeReady &&
                (addCarePathState.serviceOrganizationSettings ?? []).map((settings) => (
                    <ServiceOrganizationSettings
                        errors={errors}
                        getErrorKey={getErrorKey}
                        getServiceIndex={getServiceIndex}
                        hasServiceProviderAndServiceConsumerForOsasv={hasServiceProviderAndServiceConsumerForOsasv(
                            settings.service
                        )}
                        key={settings.serviceId}
                        onChange={handleChange}
                        onEmbloomQuestionnairesChange={handleEmbloomQuestionnairesChange}
                        onValidateOne={handleValidateOne}
                        onValidateTxcsId={handleValidateTxcsId}
                        organization={organization}
                        organizationIsRequestRecipientForSubsequentService={organizationIsRecipientForSubsequentService(
                            settings.service
                        )}
                        organizationServiceProvidersSettings={organizationServiceProvidersSettings}
                        serviceOrganizationSettings={settings}
                    />
                ))}
            {!addCarePathState.serviceOrganizationSettings && (
                <Alert appearance="info">
                    {translateKey('addCarePathSettingsLabelsServiceProvider.noOnlyReaderRole')}
                </Alert>
            )}
        </>
    );

    function handleEmbloomQuestionnairesChange(
        serviceOrganizationSettings: IServiceOrganizationSettings,
        selectedEmbloomQuestionnaires: string[]
    ) {
        const newOrganizationSettings = {
            ...serviceOrganizationSettings,
            embloomQuestionnaires: selectedEmbloomQuestionnaires,
        };

        dispatchAddCarePathAction({
            type: 'setServiceOrganizationSettings',
            serviceId: serviceOrganizationSettings.serviceId,
            serviceOrganizationSettings: newOrganizationSettings,
        });
    }

    function getServiceIndex(serviceOrganizationSettings: IServiceOrganizationSettings) {
        return addCarePathState.serviceOrganizationSettings?.findIndex(
            (organizationSettings) => organizationSettings.service.code === serviceOrganizationSettings.service.code
        );
    }

    function getErrorKey(serviceOrganizationSettings: IServiceOrganizationSettings, key: string) {
        const serviceIndex = getServiceIndex(serviceOrganizationSettings);
        if (serviceIndex === undefined || serviceIndex === -1) {
            return undefined;
        }

        return `serviceOrganizationSettings.${serviceIndex}.${key}`;
    }

    function handleChange(
        serviceOrganizationSettings: IServiceOrganizationSettings,
        key: keyof RealServiceOrganizationSettings,
        value: IServiceOrganizationSettings[keyof RealServiceOrganizationSettings]
    ) {
        const newOrganizationSettings = {
            ...serviceOrganizationSettings,
            [key]: value,
        };
        dispatchAddCarePathAction({
            type: 'setServiceOrganizationSettings',
            serviceId: serviceOrganizationSettings.serviceId,
            serviceOrganizationSettings: newOrganizationSettings,
        });
    }

    function handleValidateOne(
        serviceOrganizationSettings: IServiceOrganizationSettings,
        key: keyof RealServiceOrganizationSettings
    ) {
        const errorKey = getErrorKey(serviceOrganizationSettings, key);
        if (errorKey) {
            const validationErrors = updateValidationErrorsForOne(
                addCarePathServiceSettingsSchema,
                addCarePathState,
                errors,
                errorKey
            );
            setErrors(validationErrors);
        }
    }

    function handleValidateTxcsId(key: string) {
        if (key) {
            const validationErrors = updateValidationErrorsForOne(
                addCarePathServiceSettingsSchema,
                addCarePathState,
                errors,
                key
            );
            setErrors(validationErrors);
        }
    }

    async function load() {
        await Promise.all([loadOrganization(), loadProfileDefaultSettings()]);
    }

    async function loadOrganization() {
        const url = `/api/organizations/${addCarePathState.organizationId}`;
        const result = await abortableFetch.fetchJson(url, 'GET');
        setOrganization(result.json);
    }

    async function loadProfileDefaultSettings() {
        const profileServiceConsumerDefaultSettings: (IAddCarePathServiceConsumerSettings & {
            profileId: string;
            service: IService;
        })[] = [];
        const profileServiceProviderDefaultSettings: (IAddCarePathServiceProviderSettings & {
            profileId: string;
            service: IService;
        })[] = [];
        const profileServiceSupervisorDefaultSettings: (IAddCarePathServiceSupervisorSettings & {
            profileId: string;
            service: IService;
        })[] = [];

        for (const serviceGroup of addCarePathState.serviceGroups ?? []) {
            const assignedProfiles = getAssignedProfiles(serviceGroup);
            for (const assignedProfile of assignedProfiles) {
                const result = await abortableFetch.fetchJson(
                    `/api/profiles/${assignedProfile.id}/getDefaultSettings`,
                    'GET'
                );
                const defaultSettings: IAddCarePathSettings = result.json.items;
                if (defaultSettings.serviceConsumerSettings) {
                    profileServiceConsumerDefaultSettings.push(...defaultSettings.serviceConsumerSettings);
                }
                if (defaultSettings.serviceProviderSettings) {
                    profileServiceProviderDefaultSettings.push(...defaultSettings.serviceProviderSettings);
                }
                if (defaultSettings.serviceSupervisorSettings) {
                    profileServiceSupervisorDefaultSettings.push(...defaultSettings.serviceSupervisorSettings);
                }
            }
        }

        setServiceConsumerDefaultSettings(profileServiceConsumerDefaultSettings);
        setServiceProviderDefaultSettings(profileServiceProviderDefaultSettings);
        setServiceSupervisorDefaultSettings(profileServiceSupervisorDefaultSettings);
    }

    function prepareOrganizationServiceProvidersSettings() {
        const settings: IOrganizationServiceProvidersSettings = {};
        for (const serviceGroup of addCarePathState.serviceGroups!) {
            const assignedProfiles = getAssignedProfiles(serviceGroup);
            for (const service of serviceGroup.services ?? []) {
                let serviceProviderSettings: {
                    isBiometrician?: boolean;
                    isRequestRecipient: boolean;
                    isServiceConsumerSelector?: boolean;
                } | null = null;

                for (const assignedProfile of assignedProfiles) {
                    const defaultServiceProviderSettings = serviceProviderDefaultSettings?.find(
                        (providerSettings) =>
                            providerSettings.service.code === service.code &&
                            providerSettings.profileId === assignedProfile.id
                    );

                    if (defaultServiceProviderSettings) {
                        if (serviceProviderSettings === null) {
                            serviceProviderSettings = {
                                isRequestRecipient: false,
                                isBiometrician: service.serviceType === ServiceType.examination ? false : undefined,
                            };
                        }

                        const assignedProfileServiceProviderSettings =
                            assignedProfile.settings?.serviceProviderSettings.find(
                                (serviceProviderSettingsItem) =>
                                    serviceProviderSettingsItem.service.code === service.code
                            );

                        if (assignedProfileServiceProviderSettings) {
                            if (assignedProfileServiceProviderSettings.isRequestRecipient) {
                                serviceProviderSettings.isRequestRecipient = true;
                            }
                            if (assignedProfileServiceProviderSettings.isServiceConsumerSelector) {
                                serviceProviderSettings.isServiceConsumerSelector = true;
                            }
                        } else {
                            if (defaultServiceProviderSettings.isRequestRecipient) {
                                serviceProviderSettings.isRequestRecipient = true;
                            }
                        }

                        if (service.serviceType === ServiceType.examination) {
                            if (assignedProfileServiceProviderSettings) {
                                if (assignedProfileServiceProviderSettings.isBiometrician) {
                                    serviceProviderSettings.isBiometrician = true;
                                }
                            } else {
                                if (defaultServiceProviderSettings.isBiometrician) {
                                    serviceProviderSettings.isBiometrician = true;
                                }
                            }
                        }
                    }
                }

                if (serviceProviderSettings) {
                    settings[service.code] = serviceProviderSettings;
                }
            }
        }
        setOrganizationServiceProvidersSettings(settings);
    }

    function getAssignedProfiles(serviceGroup: IServiceGroupWithProfiles) {
        const activeProfiles: IOrganizationProfile[] = [];
        const roles = (addCarePathState.roles ?? []).filter((role) => role.serviceGroupId === serviceGroup.id);
        for (const role of roles) {
            let profile = (serviceGroup.profiles ?? []).find((profileItem) => profileItem.id === role.profileId);
            if (!profile) {
                profile = {
                    id: role.profileId,
                    name: '',
                    serviceGroupId: role.serviceGroupId,
                };
            }
            activeProfiles.push(profile);
        }

        return activeProfiles;
    }

    function initialize() {
        for (const serviceGroup of addCarePathState.serviceGroups ?? []) {
            for (const service of serviceGroup.services ?? []) {
                const isPartOfAnAssignedProfile = serviceIsPartOfAnAssignedProfile(serviceGroup, service);
                const isServiceOrganizationSettingAlreadyInState = serviceOrganizationSettingAlreadyInState(service);
                const isRequestRecipient = getIsRequestRecipientValue(service, serviceGroup);

                if (isPartOfAnAssignedProfile && !isServiceOrganizationSettingAlreadyInState) {
                    const serviceOrganizationSettings = initializeServiceOrganizationSettings(service);
                    serviceOrganizationSettings.isRequestRecipient = isRequestRecipient;
                    dispatchAddCarePathAction({
                        type: 'setServiceOrganizationSettings',
                        serviceId: service.id,
                        serviceOrganizationSettings,
                    });
                } else if (!isPartOfAnAssignedProfile && isServiceOrganizationSettingAlreadyInState) {
                    dispatchAddCarePathAction({ type: 'removeServiceOrganizationSettings', serviceId: service.id });
                } else if (isPartOfAnAssignedProfile && isServiceOrganizationSettingAlreadyInState) {
                    const serviceOrganizationSettings = addCarePathState.serviceOrganizationSettings?.find(
                        (organizationSetting) => organizationSetting.service.id === service.id
                    );
                    if (serviceOrganizationSettings) {
                        serviceOrganizationSettings.isRequestRecipient = isRequestRecipient;
                        dispatchAddCarePathAction({
                            type: 'setServiceOrganizationSettings',
                            serviceId: service.id,
                            serviceOrganizationSettings,
                        });
                    }
                }
            }
        }
        setInitializeReady(true);
    }

    function serviceIsPartOfAnAssignedProfile(serviceGroup: IServiceGroupWithProfiles, service: IService) {
        const serviceGroupRoles = (addCarePathState.roles ?? []).filter(
            (role) => role.serviceGroupId === serviceGroup.id && role.employments.length > 0
        );

        if (serviceGroupRoles.length === 0) {
            return false;
        }

        const assignedProfiles = serviceGroupRoles
            .map((role) => serviceGroup.profiles?.find((profile) => profile.id === role.profileId))
            .filter(Boolean) as IOrganizationProfile[];

        for (const profile of assignedProfiles) {
            const {
                profileConsumerSettings,
                profileProviderSettings,
                profileSupervisorSettings,
                profileServiceEmploymentSettings,
            } = getSettingsFromProfile(profile);

            if (
                profileConsumerSettings.some((o) => o.service.id === service.id) ||
                profileProviderSettings.some((o) => o.service.id === service.id) ||
                profileSupervisorSettings.some((o) => o.service.id === service.id) ||
                profileServiceEmploymentSettings.some((o) => o.service.id === service.id)
            ) {
                return true;
            }
        }

        return false;
    }

    function serviceOrganizationSettingAlreadyInState(service: IService) {
        return Boolean(
            addCarePathState.serviceOrganizationSettings?.some(
                (organizationSetting) => organizationSetting.service.code === service.code
            )
        );
    }

    function getCurrentServiceGroup(serviceId: string) {
        return (addCarePathState.serviceGroups ?? []).find((serviceGroup) =>
            (serviceGroup.services ?? []).some((service) => service.id === serviceId)
        );
    }

    function getIsRequestRecipientValue(service: IService, serviceGroup: IServiceGroupWithProfiles) {
        if (organizationIsRecipientForSubsequentService(service, serviceGroup)) {
            return true;
        }

        if (isTooAndIsProviderRoleAssigned(service, serviceGroup)) {
            return true;
        }

        return false;
    }

    function organizationIsRecipientForSubsequentService(service: IService, serviceGroup?: IServiceGroupWithProfiles) {
        serviceGroup = serviceGroup ?? getCurrentServiceGroup(service.id);

        if (!serviceGroup) {
            return false;
        }

        for (const profile of serviceGroup.profiles ?? []) {
            const serviceConsumerSetting = (profile.settings?.serviceConsumerSettings ?? []).find(
                (setting) => setting.autoSubsequentService?.succeedingService?.id === service.id
            );

            if (
                serviceConsumerSetting &&
                serviceConsumerSetting.autoSubsequentService?.providerOrganizationId ===
                    addCarePathState.organizationId &&
                !serviceConsumerSetting.autoSubsequentService?.providerPractitionerId
            ) {
                return true;
            }
        }

        return false;
    }

    function isTooAndIsProviderRoleAssigned(service: IService, serviceGroup: IServiceGroupWithProfiles) {
        if (service.code !== ServiceCode.TOO) {
            return false;
        }

        const tooProviderProfiles = (serviceGroup.profiles ?? []).filter((profile) =>
            (profile.settings?.serviceProviderSettings ?? []).some(
                (setting) => setting.service.code === ServiceCode.TOO
            )
        );

        const isTooProviderRoleAssigned = tooProviderProfiles.some((tooProviderProfile) =>
            addCarePathState.roles?.some(
                (role) => role.profileId === tooProviderProfile.id && role.employments.length > 0
            )
        );

        return isTooProviderRoleAssigned;
    }

    function hasServiceProviderAndServiceConsumerForOsasv(service: IService) {
        if (service.code !== ServiceCode.OSASV) {
            return false;
        }

        const currentServiceGroup = getCurrentServiceGroup(service.id);
        if (!currentServiceGroup) {
            return false;
        }

        const selectedProfiles = getAssignedProfiles(currentServiceGroup);

        const hasServiceConsumerForOsasv = selectedProfiles.some((profile) =>
            profile.settings?.serviceConsumerSettings.some(
                (setting) => setting.isDisabled === false && setting.service.id === service.id
            )
        );
        const hasServiceProviderForOsasv = selectedProfiles.some((profile) =>
            profile.settings?.serviceProviderSettings.some(
                (setting) => setting.isDisabled === false && setting.service.id === service.id
            )
        );

        return hasServiceConsumerForOsasv && hasServiceProviderForOsasv;
    }
}
